import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useSubsidiaryVatCodesApi } from "../../http/subsidiaryVatCodes/api";
import { UpsertSubsidiaryVatCode } from "../../http/subsidiaryVatCodes/types";
import ModalDialog from "../ui/ModalDialog";
import SubsidiaryVatCodeForm, { SubsidiaryVatCodeFormBody } from "./SubsidiaryVatCodeForm";

type EditSubsidiaryVatCodeDialogProps = {
  subVatCodeId: string;
  onClose: () => void;
  subsidiaryId?: string;
};

const EditSubVatDialog: FC<EditSubsidiaryVatCodeDialogProps> = ({
  subVatCodeId,
  onClose,
  subsidiaryId,
}: EditSubsidiaryVatCodeDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const subVatCodesApi = useSubsidiaryVatCodesApi();

  const subVatCodeSwr = subVatCodesApi.getAdminSubsidiaryVatCode(subVatCodeId, aborter.current.signal);
  const [subVatCode, isInitialized] = useDataInitializer(subVatCodeSwr.data, subVatCodeSwr.error);

  useEffect(() => {
    subVatCodeId && subVatCodeSwr.trigger();
  }, [subVatCodeId]);

  useEffect(() => {
    subVatCodeSwr.error && dispatch(showErrorNotification(i18n, subVatCodeSwr.error));
  }, [subVatCodeSwr.error]);

  const onSubmit = async (formData: SubsidiaryVatCodeFormBody): Promise<void> => {
    if (subVatCode?.id) {
      const body: UpsertSubsidiaryVatCode = {
        carCategory: formData.carCategory.value,
        vatCode: formData.vatCode,
        productCode: formData.productCode,
        subsidiaryId: subsidiaryId ?? formData.subsidiary.id,
      };

      setIsSubmitting(true);
      try {
        await subVatCodesApi.updateAdminSubVatCode(subVatCode.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.subsidiaryVatCodes.edit}>
      <SubsidiaryVatCodeForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.subsidiaryVatCodes.update || isSubmitting || !subVatCode?.id}
        isLoading={!isInitialized}
        subVatCode={subVatCode}
        isTab={!!subsidiaryId}
      />
    </ModalDialog>
  );
};

export default EditSubVatDialog;
